import React, { forwardRef, useCallback, useRef } from 'react';

import useTranslate from '@src/i18n/useTranslate';

import { PinCell, PinFieldRoot, PinInputRoot, PinMessage, type PinCodeInputStatus } from './styles';

export type { PinCodeInputStatus };

export type PinCodeInputMode = 'numeric' | 'alphanumeric' | 'custom';

export type PinCharPattern = RegExp | ((index: number) => RegExp);

export type PinCodeInputModeConfig = {
  allowedChar: PinCharPattern;
  inputMode?: 'numeric' | 'text';
};

type PinCodeInputBaseProps = {
  value?: string;
  name?: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
  status?: PinCodeInputStatus;
  message?: string;
  length?: number;
};

type PinCodeInputModeProps =
  | { mode?: Exclude<PinCodeInputMode, 'custom'>; modeConfig?: never }
  | { mode: 'custom'; modeConfig: PinCodeInputModeConfig };

export type PinCodeInputProps = PinCodeInputBaseProps & PinCodeInputModeProps;

const BUILT_IN_MODES: Record<Exclude<PinCodeInputMode, 'custom'>, PinCodeInputModeConfig> = {
  numeric: { allowedChar: /[0-9]/, inputMode: 'numeric' },
  alphanumeric: { allowedChar: /[a-zA-Z0-9]/, inputMode: 'text' },
};

const resolveModeConfig = (mode: PinCodeInputMode, modeConfig?: PinCodeInputModeConfig): PinCodeInputModeConfig => {
  if (mode === 'custom') return modeConfig ?? BUILT_IN_MODES.alphanumeric;
  return BUILT_IN_MODES[mode];
};

const acceptsChar = (config: PinCodeInputModeConfig, char: string, index: number) => {
  const pattern = typeof config.allowedChar === 'function' ? config.allowedChar(index) : config.allowedChar;

  // A caller-provided pattern may carry the global flag, whose lastIndex would make test() alternate between calls.
  pattern.lastIndex = 0;

  return pattern.test(char);
};

const parseChars = (value: string, length: number, config: PinCodeInputModeConfig) =>
  Array.from({ length }, (_, index) => {
    const char = value[index];
    return char !== undefined && acceptsChar(config, char, index) ? char : '';
  });

const encodeChars = (charArray: string[]) =>
  charArray
    .map((char) => char || ' ')
    .join('')
    .replace(/ +$/, '');

const resolveStatus = (status: PinCodeInputStatus | undefined, error?: boolean): PinCodeInputStatus => {
  if (status) return status;
  if (error) return 'error';
  return 'default';
};

export const PinCodeInput = forwardRef<HTMLInputElement, PinCodeInputProps>((props, ref) => {
  const { value = '', onChange, disabled, length = 6, name, error, status, message } = props;
  const { mode = 'numeric', modeConfig } = props;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { i18n } = useTranslate();

  const config = resolveModeConfig(mode, modeConfig);
  const resolvedStatus = resolveStatus(status, error);
  const chars = parseChars(value, length, config);
  const showMessage = Boolean(message);
  const isInvalid = resolvedStatus === 'error';

  const focusCell = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  const updateValue = useCallback((nextChars: string[]) => onChange(encodeChars(nextChars)), [onChange]);

  const setCellRef = (index: number) => (element: HTMLInputElement | null) => {
    inputRefs.current[index] = element;

    if (index !== 0) return;

    if (typeof ref === 'function') {
      ref(element);
      return;
    }

    if (ref) {
      ref.current = element;
    }
  };

  const handleChange = (index: number, inputValue: string) => {
    const accepted = Array.from(inputValue).filter((char) => acceptsChar(config, char, index));
    const char = accepted[accepted.length - 1] ?? '';
    const nextChars = [...chars];
    nextChars[index] = char;
    updateValue(nextChars);

    if (char && index < length - 1) {
      focusCell(index + 1);
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !chars[index] && index > 0) {
      event.preventDefault();
      focusCell(index - 1);
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusCell(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusCell(index + 1);
    }
  };

  const handlePaste = (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');

    if (!pasted) return;

    const hasAnyValue = chars.some((char) => char !== '');
    const firstEmptyIndex = chars.findIndex((char) => char === '');

    let startIndex: number;
    if (index === 0 || !hasAnyValue) {
      startIndex = 0;
    } else {
      startIndex = firstEmptyIndex === -1 ? index : firstEmptyIndex;
    }

    const nextChars = [...chars];
    let targetIndex = startIndex;

    Array.from(pasted).forEach((char) => {
      if (targetIndex >= length || !acceptsChar(config, char, targetIndex)) return;

      nextChars[targetIndex] = char;
      targetIndex += 1;
    });

    if (targetIndex === startIndex) return;

    updateValue(nextChars);
    focusCell(targetIndex - 1);
  };

  return (
    <PinFieldRoot data-testid='pin-code-input'>
      <PinInputRoot>
        {chars.map((char, index) => (
          <PinCell
            key={index}
            ref={setCellRef(index)}
            type='text'
            inputMode={config.inputMode ?? 'text'}
            autoComplete='off'
            autoFocus={index === 0}
            maxLength={1}
            value={char}
            disabled={disabled}
            status={resolvedStatus}
            aria-invalid={isInvalid}
            name={index === 0 ? name : undefined}
            aria-label={i18n('code_input_value_label', { index: index + 1, length })}
            onChange={(event) => handleChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={(event) => handlePaste(index, event)}
            onFocus={(event) => event.target.select()}
          />
        ))}
      </PinInputRoot>
      {showMessage ? <PinMessage status={resolvedStatus}>{message}</PinMessage> : null}
    </PinFieldRoot>
  );
});

PinCodeInput.displayName = 'PinCodeInput';

export default PinCodeInput;
